import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ClipboardList, LayoutList, User } from 'lucide-react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useTicketViewStore } from '@/store/ticketView'
import DashboardSection from '@/components/dashboard/DashboardSection'
import TicketQueueStrip from '@/components/dashboard/TicketQueueStrip'
import TicketPulseStrip from '@/components/dashboard/TicketPulseStrip'
import TicketStatusStrip from '@/components/dashboard/TicketStatusStrip'
import TicketSourceCard from '@/components/dashboard/TicketSourceCard'
import TaskSlaSection from '@/components/dashboard/TaskSlaSection'
import TaskAgingCard from '@/components/dashboard/TaskAgingCard'
import TrendsCard from '@/components/dashboard/TrendsCard'
import AgentPerformanceCard from '@/components/dashboard/AgentPerformanceCard'
import ReportQuickLinks from '@/components/dashboard/ReportQuickLinks'

function formatRefreshedAt(date: Date | null): string {
  if (!date) return 'Loading…'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function scrollToId(id: string) {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

export default function DashboardPage() {
  const { ticketStats, taskSla, ticketLoading, taskSlaLoading, refreshedAt } = useDashboardData()
  const quickLinkSize = useBreakpointValue({ base: 'xs', md: 'sm' }) as 'xs' | 'sm' | undefined
  const navigate = useNavigate()
  const setTicketView = useTicketViewStore((s) => s.setView)

  const openMyTickets = () => {
    setTicketView('my')
    navigate('/tickets')
  }

  const quickLinks: {
    label: string
    icon: typeof ClipboardList
    onClick?: () => void
    to?: string
  }[] = [
    { label: 'Action queue', icon: ClipboardList, onClick: () => scrollToId('action-queue') },
    { label: 'Ticket status', icon: LayoutList, onClick: () => scrollToId('ticket-status') },
    { label: 'My Tickets', icon: User, onClick: openMyTickets },
  ]

  return (
    <Box
      pl={{ base: 3, md: 4 }}
      pr={{ base: 2, md: 3 }}
      pt={{ base: 3, md: 4 }}
      pb={{ base: 3, md: 4 }}
      h="100%"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      w="100%"
      minW={0}
    >
      <VStack
        spacing={{ base: 3, md: 4 }}
        align="stretch"
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        minH={0}
        w="100%"
        pr={{ base: 2, md: 3 }}
        pb={2}
        sx={{ scrollbarGutter: 'stable' }}
      >
        <Flex
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={3}
          flexWrap="wrap"
          flexShrink={0}
        >
          <Box minW={0}>
            <Heading size="md" m={0} lineHeight="1.2">
              Operations home
            </Heading>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              Updated {formatRefreshedAt(refreshedAt)}
            </Text>
          </Box>
          <HStack spacing={2} flexWrap="wrap">
            {quickLinks.map(({ label, icon: Icon, onClick, to }) => (
              <Button
                key={label}
                as={to ? RouterLink : undefined}
                to={to}
                onClick={onClick}
                size={quickLinkSize}
                variant="outline"
                colorScheme="blue"
                leftIcon={<Icon size={14} />}
                fontWeight="medium"
                minH="36px"
              >
                {label}
              </Button>
            ))}
          </HStack>
        </Flex>

        <Grid
          templateColumns={{ base: '1fr', lg: '1.15fr 1fr' }}
          gap={3}
          flexShrink={0}
          w="100%"
          minW={0}
        >
          <Box id="action-queue" scrollMarginTop={4}>
            <DashboardSection title="Action queue" actionLabel="Open tickets →" actionTo="/tickets" mb={0}>
              <TicketQueueStrip stats={ticketStats} loading={ticketLoading} />
            </DashboardSection>
          </Box>
          <DashboardSection title="Today's pulse" mb={0}>
            <TicketPulseStrip stats={ticketStats} loading={ticketLoading} />
          </DashboardSection>
        </Grid>

        <Box id="ticket-status" scrollMarginTop={4}>
          <DashboardSection title="Ticket status" actionLabel="Open tickets →" actionTo="/tickets">
            <TicketStatusStrip stats={ticketStats} loading={ticketLoading} />
          </DashboardSection>
        </Box>

        <TaskSlaSection data={taskSla} loading={taskSlaLoading} />

        <Grid
          templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr 1fr 2fr' }}
          gap={3}
          flexShrink={0}
          w="100%"
          minW={0}
        >
          <TicketSourceCard source={ticketStats.ticket_source} loading={ticketLoading} />
          <TaskAgingCard data={taskSla} loading={taskSlaLoading} />
          <TrendsCard stats={ticketStats} loading={ticketLoading} />
        </Grid>

        <AgentPerformanceCard agents={ticketStats.agent_performance} loading={ticketLoading} />

        <ReportQuickLinks />
      </VStack>
    </Box>
  )
}
